// src/components/auth/AuthScreen.tsx
import React, { useState, useEffect } from 'react';
import { Phone, User, Mail, Lock, ArrowLeft, ChevronDown, Search, CheckCircle, X, Gift } from 'lucide-react';
import { Button } from '../shared/Button';
import { UserProfile } from '../../types';
import { auth, googleProvider, db } from '../../services/firebase';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile as updateFirebaseProfile,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions'; 
import { generateAvatarUrl } from '../../utils/helpers';

declare global {
  interface Window {
    recaptchaVerifier: any;
  }
}

// Common country codes
const COUNTRY_CODES = [
  { code: 'ZA', dial: '+27', flag: '🇿🇦', name: 'South Africa' },
  { code: 'US', dial: '+1', flag: '🇺🇸', name: 'United States' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'NG', dial: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: 'KE', dial: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: 'GH', dial: '+233', flag: '🇬🇭', name: 'Ghana' },
  { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'India' },
];

interface AuthScreenProps {
  onComplete: (profile: UserProfile) => void;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthScreenProps> = ({ onComplete, onClose }) => {

  // Mode State
  const [isLogin, setIsLogin] = useState(true);
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  
  // Country Picker State
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // ✅ New: Phone for email signup
  const [phone, setPhone] = useState(''); 
  const [otp, setOtp] = useState('');
  const [referralInput, setReferralInput] = useState('');
  
  // App State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Phone Auth State
  const [verificationId, setVerificationId] = useState<ConfirmationResult | null>(null);
  const [otpSent, setOtpSent] = useState(false);

  const filteredCountries = COUNTRY_CODES.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.dial.includes(searchQuery)
  );

  // 🟢 CREATE USER PROFILE - WITH PHONE NUMBER
  const createUserProfile = (userData: any): UserProfile => {
    const referredBy = referralInput.trim() || localStorage.getItem('scoop_referrer') || null;

    // ✅ Get phone from multiple sources
    const userPhone = userData.phoneNumber || phoneNumber || phone || '';

    const newProfile: any = {
      id: userData.uid,
      uid: userData.uid,
      email: userData.email || email,
      displayName: name || userData.displayName || 'User',
      handle: `@${(name || userData.displayName || 'user').replace(/\s/g, '').toLowerCase() + Math.floor(Math.random() * 1000)}`,
      bio: 'Welcome to Scoop! Ready to discover your style.',
      avatar: userData.photoURL || generateAvatarUrl(name || userData.displayName || 'User'),
      
      // ✅ FIX: Save phone number correctly
      phoneNumber: userPhone,
      phone: userPhone,
      
      cred: 100,
      referredBy: referredBy,
      followers: [],
      following: 0,
      stylePreferences: [],
      wishlist: [],
      savedProducts: [],
      savedPosts: [],
      followingUsers: [],
      blockedUsers: [],
      friendRequests: [],
      accountType: 'user',
      hasSetupProfile: false,
      settings: {
        notifications: true,
        privacy: false,
        currency: 'ZAR',
        theme: 'dark',
        language: 'en',
        marketingEmails: true
      },
      shippingAddresses: [],
      paymentMethods: [],
      membership: { tier: 'free' }
    };

    if (localStorage.getItem('scoop_referrer')) {
      localStorage.removeItem('scoop_referrer');
    }

    return newProfile as UserProfile;
  };

  const saveUserToFirestore = async (user: any, profile: UserProfile) => {
    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, {
      ...profile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  // --- EMAIL AUTH HANDLER ---
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // ✅ Validation - Phone required for signup
    if (!email || !password) { setError('Please fill in all fields'); return; }
    if (!isLogin && !name) { setError('Name is required'); return; }
    if (!isLogin && !phoneNumber) { setError('Phone number is required'); return; }
    if (!isLogin && phoneNumber.replace(/\D/g, '').length < 9) { 
      setError('Please enter a valid phone number (min 9 digits)'); 
      return; 
    }
    if (password.length < 6) { setError('Password too short'); return; }

    setLoading(true);
    
    try {
      if (isLogin) {
        // --- NORMAL LOGIN FLOW ---
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
        
        if (userDoc.exists()) {
            onComplete(userDoc.data() as UserProfile);
            onClose(); 
        } else {
            const profile = createUserProfile(userCredential.user);
            await saveUserToFirestore(userCredential.user, profile);
            onComplete(profile);
            onClose(); 
        }
      } else {
        // --- CREATE ACCOUNT FLOW ---
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateFirebaseProfile(userCredential.user, { displayName: name });
        
        // ✅ Pass phone number to profile
        const profile = createUserProfile({
          ...userCredential.user,
          phoneNumber: phoneNumber
        });
        
        await saveUserToFirestore(userCredential.user, profile);
        
        try {
            const functions = getFunctions();
            const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
            sendWelcomeEmail({
                targetEmail: email,
                userName: name
            }).then(() => console.log("Welcome email sent successfully."))
              .catch(err => console.error("Email failed:", err));
        } catch (emailErr) {
            console.error("Function call failed", emailErr);
        }

        onComplete(profile); 
        onClose(); 
      }
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use' && !isLogin) {
        try {
          const loginCredential = await signInWithEmailAndPassword(auth, email, password);
          const userDoc = await getDoc(doc(db, "users", loginCredential.user.uid));
          
          if (userDoc.exists()) {
             onComplete(userDoc.data() as UserProfile);
             onClose(); 
          } else {
             const profile = createUserProfile(loginCredential.user);
             await saveUserToFirestore(loginCredential.user, profile);
             onComplete(profile);
             onClose(); 
          }
        } catch (loginErr: any) {
          if (loginErr.code === 'auth/wrong-password' || loginErr.code === 'auth/invalid-credential') {
             setError('Account already exists. Please sign in with the correct password.');
          } else {
             handleAuthErrors(loginErr);
          }
        }
      } else {
        handleAuthErrors(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // --- GOOGLE AUTH HANDLER ---
  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        onComplete(userSnap.data() as UserProfile);
        onClose(); 
      } else {
        const finalProfile = createUserProfile(user);
        await saveUserToFirestore(user, finalProfile);
        
        try {
            const functions = getFunctions();
            const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
            sendWelcomeEmail({
                targetEmail: user.email,
                userName: user.displayName
            }).catch(err => console.error("Email failed:", err));
        } catch (emailErr) {
            console.error("Function call failed", emailErr);
        }

        onComplete(finalProfile);
        onClose(); 
      }
    } catch (err: any) {
      console.error('Google auth error:', err);
      handleAuthErrors(err);
    } finally {
      setLoading(false);
    }
  };

  // --- PHONE AUTH LOGIC ---
  const setupRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (e) {}
      window.recaptchaVerifier = undefined;
    }
    const container = document.getElementById('recaptcha-container');
    if (container) container.innerHTML = '';

    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      'size': 'invisible',
      'callback': () => { }
    });
  };

  const handleSendOtp = async () => {
    const cleanPhone = phone.replace(/^0+/, ''); 
    const fullNumber = `${selectedCountry.dial}${cleanPhone}`;

    if (cleanPhone.length < 9) { 
      setError('Please enter a valid phone number (min 9 digits)'); 
      return; 
    }
    
    setError('');
    setLoading(true);
    
    try {
      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;
      const confirmationResult = await signInWithPhoneNumber(auth, fullNumber, appVerifier);
      setVerificationId(confirmationResult);
      setOtpSent(true);
    } catch (err: any) {
      if (err.code === 'auth/billing-not-enabled') {
          setError('Project limit reached. Use Test Number: 082 000 0000 / 123456');
      } else {
          setError(`Failed to send SMS: ${err.message}`);
      }
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = undefined;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || !verificationId) return;
    setLoading(true);
    setError('');

    try {
      const result = await verificationId.confirm(otp);
      const user = result.user;
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      
      // ✅ Save the full phone number
      const fullPhoneNumber = `${selectedCountry.dial}${phone.replace(/^0+/, '')}`;
      
      if (userSnap.exists()) {
        // Update existing user's phone number
        await updateDoc(userRef, {
          phoneNumber: fullPhoneNumber,
          phone: fullPhoneNumber
        });
        const updatedData = (await getDoc(userRef)).data();
        onComplete(updatedData as UserProfile);
        onClose();
      } else {
        const finalProfile = createUserProfile({
          ...user,
          phoneNumber: fullPhoneNumber
        });
        await saveUserToFirestore(user, finalProfile);
        onComplete(finalProfile);
        onClose();
      }
    } catch (err) {
      setError('Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthErrors = (err: any) => {
    const errorCode = err.code;
    if (errorCode === 'auth/email-already-in-use') setError('Email already registered.');
    else if (errorCode === 'auth/invalid-email') setError('Invalid email address.');
    else if (errorCode === 'auth/user-not-found' || errorCode === 'auth/wrong-password' || errorCode === 'auth/invalid-credential') setError('Invalid email or password.');
    else if (errorCode === 'auth/popup-closed-by-user') setError('Sign-in cancelled.');
    else if (errorCode === 'auth/network-request-failed') setError('Network error. Check connection.');
    else setError(`Error: ${errorCode || err.message}`);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-0 lg:p-8 animate-in fade-in duration-300">
      
      {/* Modal Box */}
      <div className="relative w-full h-full lg:h-[700px] lg:max-w-5xl lg:rounded-3xl bg-black border border-white/10 shadow-2xl flex overflow-hidden animate-in slide-in-from-bottom-8 duration-500">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 z-[210] p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
        >
          <X size={20} />
        </button>

        {/* LEFT PANEL */}
        <div className="hidden lg:flex relative w-[50%] border-r border-white/20 bg-zinc-900 items-end p-12 overflow-hidden">
           <img 
              src="/scoop banner image.jpg" 
              className="absolute inset-0 w-full h-full object-cover grayscale opacity-40 hover:scale-105 hover:grayscale-0 transition-all duration-[2s]" 
              alt="Scoophype"
           />
           <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
           
           <div className="relative z-10 w-full">
              <span className="text-white/70 font-mono tracking-widest uppercase text-xs mb-4 block">Masterarts Connect</span>
              <h1 className="text-[7rem] leading-[0.8] font-black italic tracking-tighter uppercase text-white mb-6">SCOOP.</h1>
              <p className="text-sm font-bold uppercase tracking-widest text-zinc-400 max-w-sm leading-relaxed border-l-2 border-white pl-4">
                Create Collaborate Dominate
              </p>
           </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="w-full lg:w-[50%] flex flex-col h-full overflow-y-auto relative bg-black">
           
           <div className="flex-1 flex flex-col justify-center p-8 lg:px-12 lg:py-12 max-w-xl mx-auto w-full">
              
              <div id="recaptcha-container"></div>

              {/* Mobile Logo */}
              <div className="lg:hidden mb-12 text-center mt-8">
                <h1 className="text-5xl font-black text-white tracking-tighter mb-2 italic uppercase">Scoop<span className="text-white/50">.</span></h1>
                <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.3em]">The streets are watching</p>
              </div>

              {/* --- COUNTRY PICKER --- */}
              {showPhoneAuth && isCountryPickerOpen ? (
                <div className="flex-1 flex flex-col animate-in slide-in-from-right duration-200">
                  <div className="mb-6 flex items-center gap-4">
                    <button onClick={() => setIsCountryPickerOpen(false)} className="hover:text-white/50 transition-colors">
                      <ArrowLeft className="text-white"/>
                    </button>
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                      <input 
                        autoFocus
                        type="text"
                        placeholder="Search country"
                        className="w-full bg-white/10 rounded-xl py-4 pl-10 pr-4 text-white text-sm outline-none focus:bg-white/20 transition-colors"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-2">
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        onClick={() => { setSelectedCountry(country); setIsCountryPickerOpen(false); setSearchQuery(''); }}
                        className="w-full flex items-center gap-4 p-4 hover:bg-white/5 rounded-xl transition-colors text-left border border-transparent hover:border-white/10"
                      >
                        <span className="text-2xl">{country.flag}</span>
                        <div className="flex-1">
                          <p className="text-white font-medium">{country.name}</p>
                          <p className="text-white/40 text-sm">{country.dial}</p>
                        </div>
                        {selectedCountry.code === country.code && <CheckCircle size={16} className="text-green-500" />}
                      </button>
                    ))}
                  </div>
                </div>

              // --- PHONE AUTH FORM ---
              ) : showPhoneAuth ? (
                <div className="animate-in fade-in duration-300">
                  <div className="mb-8">
                     <button onClick={() => setShowPhoneAuth(false)} className="text-white/50 hover:text-white flex items-center gap-2 mb-8 transition-colors text-xs font-bold uppercase tracking-widest">
                       <ArrowLeft size={16} /> Back
                     </button>
                     <h2 className="text-3xl lg:text-4xl font-black text-white mb-2 uppercase italic">{otpSent ? 'Verify Code' : 'Phone Sign In'}</h2>
                     <p className="text-white/60 text-sm">{otpSent ? `Enter the code sent to ${selectedCountry.dial} ${phone}` : 'We will send you a text with a verification code.'}</p>
                  </div>
                  
                  <div className="space-y-4">
                    {!otpSent ? (
                      <div className="space-y-2">
                         <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Phone Number</label>
                         <div className="flex gap-3">
                            <button onClick={() => setIsCountryPickerOpen(true)} className="flex items-center gap-2 bg-zinc-900 border border-white/20 rounded-xl px-4 py-4 hover:border-white transition-colors">
                              <span className="text-xl">{selectedCountry.flag}</span>
                              <span className="text-white font-bold">{selectedCountry.dial}</span>
                              <ChevronDown size={14} className="text-white/40" />
                            </button>
                            <input
                              type="tel"
                              inputMode="numeric"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                              placeholder="82 123 4567" 
                              className="flex-1 bg-black border border-white/20 rounded-xl p-4 text-white placeholder-white/20 outline-none focus:border-white text-lg font-medium transition-colors"
                              disabled={loading}
                            />
                         </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Verification Code</label>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          placeholder="123456" 
                          className="w-full bg-black border border-white/20 rounded-xl p-4 text-white outline-none focus:border-white text-center tracking-[1em] text-2xl font-mono transition-colors"
                          maxLength={6}
                        />
                      </div>
                    )}
                  </div>
                  
                  {error && <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30"><p className="text-red-500 font-bold text-xs text-center">{error}</p></div>}

                  <div className="mt-8 space-y-4">
                    <Button fullWidth variant="primary" className="h-16 text-sm font-black uppercase tracking-widest rounded-xl hover:scale-[1.02] transition-transform" onClick={otpSent ? handleVerifyOtp : handleSendOtp} loading={loading} disabled={loading}>
                      {otpSent ? 'Verify Code' : 'Send Code'}
                    </Button>
                    {otpSent && (
                       <button onClick={() => { setOtpSent(false); setOtp(''); }} className="w-full text-center text-white/60 hover:text-white text-xs font-bold uppercase tracking-widest mt-4">
                         Change Phone Number
                       </button>
                    )}
                  </div>
                </div>

              // --- EMAIL / GOOGLE AUTH FORM ---
              ) : (
                <div className="animate-in fade-in duration-300">
                  <div className="hidden lg:block mb-10">
                     <h2 className="text-4xl font-black text-white mb-2 uppercase italic">{isLogin ? 'Sign In' : 'Create Account'}</h2>
                     <p className="text-zinc-500 text-sm font-bold uppercase tracking-widest">Enter your details to continue.</p>
                  </div>

                  <form onSubmit={handleEmailAuth} className="space-y-4">
                    {!isLogin && (
                      <>
                        {/* Name Field */}
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Full Name *</label>
                          <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                            <input 
                              type="text" 
                              value={name} 
                              onChange={e => setName(e.target.value)} 
                              className="w-full bg-zinc-900/50 border border-white/10 rounded-xl p-4 pl-12 text-white outline-none focus:border-white focus:bg-black transition-all placeholder:text-white/20" 
                              placeholder="e.g. Neo Kgosana" 
                              disabled={loading} 
                              required 
                            />
                          </div>
                        </div>
                        
                        {/* ✅ NEW: Phone Number Field - Required for Signup */}
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Phone Number *</label>
                          <div className="relative">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                            <input 
                              type="tel" 
                              value={phoneNumber} 
                              onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))} 
                              className="w-full bg-zinc-900/50 border border-white/10 rounded-xl p-4 pl-12 text-white outline-none focus:border-white focus:bg-black transition-all placeholder:text-white/20" 
                              placeholder="82 123 4567" 
                              disabled={loading} 
                              required
                            />
                          </div>
                          <p className="text-[10px] text-zinc-500">We'll send order updates to this number</p>
                        </div>
                      </>
                    )}
                    
                    {/* Email Field */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                        <input 
                          type="email" 
                          value={email} 
                          onChange={e => setEmail(e.target.value)} 
                          className="w-full bg-zinc-900/50 border border-white/10 rounded-xl p-4 pl-12 text-white outline-none focus:border-white focus:bg-black transition-all placeholder:text-white/20" 
                          placeholder="name@example.com" 
                          disabled={loading} 
                          required 
                        />
                      </div>
                    </div>
                    
                    {/* Password Field */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Password *</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                        <input 
                          type="password" 
                          value={password} 
                          onChange={e => setPassword(e.target.value)} 
                          className="w-full bg-zinc-900/50 border border-white/10 rounded-xl p-4 pl-12 text-white outline-none focus:border-white focus:bg-black transition-all placeholder:text-white/20" 
                          placeholder="••••••••" 
                          disabled={loading} 
                          minLength={6} 
                          required 
                        />
                      </div>
                    </div>

                    {/* Referral Code Input */}
                    {!isLogin && (
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-white/70 uppercase tracking-widest">Referral Code (Optional)</label>
                        <div className="relative">
                          <Gift className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                          <input 
                            type="text" 
                            value={referralInput} 
                            onChange={e => setReferralInput(e.target.value.toUpperCase())} 
                            className="w-full bg-zinc-900/50 border border-white/10 rounded-xl p-4 pl-12 text-white outline-none focus:border-white focus:bg-black transition-all placeholder:text-white/20 font-mono tracking-widest" 
                            placeholder="e.g. SCOOP24" 
                            disabled={loading} 
                          />
                        </div>
                      </div>
                    )}
                    
                    {error && <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30"><p className="text-red-500 font-bold text-xs text-center">{error}</p></div>}
                    
                    <div className="mt-8 space-y-4">
                      <Button type="submit" fullWidth variant="primary" loading={loading} disabled={loading} className="h-16 text-sm font-black uppercase tracking-widest rounded-xl hover:scale-[1.02] transition-transform">
                        {isLogin ? 'Sign In' : 'Create Account'}
                      </Button>
                      
                      <div className="relative flex py-4 items-center">
                        <div className="flex-grow border-t border-white/10"></div>
                        <span className="flex-shrink-0 mx-4 text-zinc-500 text-[10px] font-black uppercase tracking-widest">Or continue with</span>
                        <div className="flex-grow border-t border-white/10"></div>
                      </div>
                      
                      <button type="button" onClick={handleGoogleLogin} disabled={loading} className="w-full h-14 rounded-xl bg-white text-black font-bold flex items-center justify-center gap-3 hover:bg-zinc-200 transition-colors disabled:opacity-50">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Google
                      </button>
                      
                      <button type="button" onClick={() => setShowPhoneAuth(true)} disabled={loading} className="w-full h-14 rounded-xl bg-zinc-900 border border-white/10 text-white font-bold flex items-center justify-center gap-3 hover:border-white transition-colors disabled:opacity-50">
                        <Phone size={20} />
                        Phone Number
                      </button>
                    </div>
                  </form>
                  
                  <button type="button" onClick={() => setIsLogin(!isLogin)} disabled={loading} className="w-full mt-8 text-zinc-500 text-[10px] font-black uppercase tracking-[0.2em] text-center hover:text-white transition-colors disabled:opacity-50">
                    {isLogin ? "Join the movement." : "Already in the circle? Sign In."}
                  </button>
                </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
};